import {useEffect, useState, useCallback} from "react";
import {ChatModel} from "@/app/api/chat";
import {useNavigate} from "react-router-dom";
import {ChatRequests} from "@/app/api/chat/requests";
import {SearchParamsKey, useUrlParams} from "@/app/utils/use_search_params";
import {MessagesRequests} from "@/app/api/messages/requests";
import {MessageModel, SendMessageRequestModel} from "@/app/api/messages";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {NewUserModel, UserRequests} from "@/app/api/user";
import useChatSocket from "@/app/services/chat_soket";
import {useDispatch} from "react-redux";
import {AppDispatch} from "@/app/store";
import {decreaseChatUnreadCount} from "@/app/features/unread_count/slice";

export interface ScreenState {
  hasError?: string;
  chats?: ChatModel[];
  page: number;
  chatPage: number;
  messages?: MessageModel[];
  selectedChat?: ChatModel;
  user?: NewUserModel;
  loading: boolean;
  hasMore: boolean;
}

const useChats = () => {
  const navigate = useNavigate();
  const {getUrlParam, setUrlParam} = useUrlParams();
  const dispatch = useDispatch<AppDispatch>();
  const {connect, subscribeToChatListUpdates, subscribeToChatUpdates, connected} = useChatSocket();

  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    chats: undefined,
    page: 1,
    chatPage: 1,
    messages: undefined,
    selectedChat: undefined,
    user: undefined,
    loading: false,
    hasMore: true,
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const {page, chatPage} = state;
  const chatId = getUrlParam(SearchParamsKey.chat);

  const regCallBack = useCallback((userId: number) => {
    subscribeToChatListUpdates(userId, (chat: ChatModel) => {
      const chats = state.chats;
      if (chats) {
        const index = chats.findIndex((c) => c.id === chat.id);
        if (index !== -1) {
          chats[index] = chat;
        }
      }
      if (chats) {
        updateState({chats: chats});
      }
    })
  }, [updateState]);

  const init = useCallback(async () => {
    const user = await UserRequests.getCurrentUser();
    connect(() => regCallBack(user.user.id));
    updateState({user});
  }, [updateState, regCallBack, connect]);

  useEffect(() => {
    init();
  }, [updateState]);

  const addMessageToStart = useCallback((message: MessageModel) => {
    const messages = state.messages ?? [];
    messages.unshift(message);
    updateState({messages});
  }, [state.messages, updateState]);

  useEffect(() => {
    if (connected && state.selectedChat?.id) {
      const onMessage = (message: MessageModel) => {
        const messages = state.messages ?? [];

        // Avoid duplicates if this message is already in the list
        if (messages.some(m => m.id === message.id)) {
          return;
        }

        const firstMessage = messages[0];

        if (
          firstMessage?.id === 0 &&
          firstMessage.content === message.content &&
          firstMessage.authorId === message.authorId &&
          firstMessage.chatId === message.chatId
        ) {
          const updatedMessages = [...messages];
          updatedMessages[0] = message;
          updateState({messages: updatedMessages});
        } else {
          addMessageToStart(message);
        }
      };
      subscribeToChatUpdates(state.selectedChat.id, onMessage);
    }
  }, [connected, state.selectedChat?.id, state.messages, updateState, addMessageToStart]);

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        updateState({loading: true});
        const newChats = await ChatRequests.getChats(page);
        if (!isActive) return;
        
        if (page === 1) {
          // Первая загрузка - заменяем список
          updateState({
            chats: newChats,
            loading: false,
            hasMore: newChats.length === 10 // Если получили меньше 10, значит это последняя страница
          });
        } else {
          // Последующие загрузки - добавляем к существующему списку
          setState(prev => ({
            ...prev,
            chats: [...(prev.chats || []), ...newChats],
            loading: false,
            hasMore: newChats.length === 10
          }));
        }
      } catch (err: any) {
        if (!isActive) return;
        updateState({hasError: err.message, loading: false});
      }
    })();
    return () => {
      isActive = false
    }
  }, [page, updateState]);

  // Ensure selectedChat follows URL and chat list, without reloading messages on every chats change
  useEffect(() => {
    if (!chatId || !state.chats) {
      updateState({selectedChat: undefined, messages: undefined});
      return;
    }

    const chat = state.chats.find(c => c.id.toString() === chatId);
    if (!chat) {
      updateState({selectedChat: undefined, messages: undefined});
      return;
    }

    // If already selected, do nothing to avoid unnecessary reloads
    if (state.selectedChat?.id === chat.id) {
      return;
    }

    // When URL/chat list change, select chat and reset messages to trigger load
    updateState({selectedChat: chat, messages: undefined});
  }, [chatId, state.chats, state.selectedChat?.id, updateState]);

  // Load messages when selectedChat or page changes, and merge with optimistic messages
  useEffect(() => {
    if (!state.selectedChat) {
      return;
    }

    const currentChatId = state.selectedChat.id;
    let isActive = true;

    (async () => {
      try {
        const serverMessages = await MessagesRequests.getMessages(currentChatId, chatPage);
        if (!isActive) return;

        setState(prev => {
          // If selected chat changed while we were loading, don't overwrite
          if (!prev.selectedChat || prev.selectedChat.id !== currentChatId) {
            return prev;
          }

          const existingMessages = prev.messages ?? [];

          // Keep optimistic messages (id === 0) for this chat that are not yet in server list
          const optimisticMessages = existingMessages.filter(
            m => m.id === 0 && m.chatId === currentChatId
          );

          const filteredOptimistic = optimisticMessages.filter(o =>
            !serverMessages.some(
              s =>
                s.chatId === o.chatId &&
                s.authorId === o.authorId &&
                s.content === o.content
            )
          );

          return {
            ...prev,
            messages: [...filteredOptimistic, ...serverMessages]
          };
        });
      } catch (err: any) {
        if (!isActive) return;
        updateState({hasError: err.message});
      }
    })();

    return () => {
      isActive = false;
    };
  }, [state.selectedChat?.id, chatPage, updateState]);

  const search = useCallback(
    async (query: string) => {
      if (query === "") {
        // При очистке поля поиска показываем все чаты
        const allChats = await ChatRequests.getChats(1);
        updateState({chats: allChats, page: 1});
        return;
      }
      const byName = await ChatRequests.getChatByUsername(query);
      updateState({chats: byName});
    },
    [updateState]
  );

  const goToMain = useCallback(() => {
    navigate("/findPlayers");
  }, [navigate]);

  const selectChat = useCallback(
    (chat: ChatModel) => {
      if (state.selectedChat?.id === chat.id) return;
      
      // Dispatch Redux action to decrease AppBar unread count immediately
      if (chat.unreadMessageCount > 0) {
        dispatch(decreaseChatUnreadCount(chat.unreadMessageCount));
      }
      
      // Mark messages as read by setting unreadMessageCount to 0 for the selected chat
      const updatedChats = state.chats?.map(c => 
        c.id === chat.id ? {...c, unreadMessageCount: 0} : c
      );
      
      updateState({
        selectedChat: {...chat, unreadMessageCount: 0}, 
        messages: undefined,
        chats: updatedChats
      });
      setUrlParam(SearchParamsKey.chat, chat.id.toString());
    },
    [setUrlParam, state.selectedChat?.id, state.chats, updateState, dispatch]
  );

  const goBack = useCallback(() => {
    navigate(-1);
    updateState({selectedChat: undefined, messages: undefined});
  }, [navigate, updateState]);

  const sendMessage = useCallback(async (message: string) => {
    if (state.selectedChat && state.user?.user) {
      const data: SendMessageRequestModel = {
        chatId: state.selectedChat.id,
        authorId: state.user.user.id,
        content: message
      };

      const currentTime = new Date();

      const optimisticMessage: MessageModel = {
        chatId: state.selectedChat.id,
        authorId: state.user.user.id,
        authorUsername: state.user.user.username ?? '',
        content: message,
        sentAt: currentTime.toISOString(),
        id: 0
      };

      addMessageToStart(optimisticMessage);

      // Update the last message in the chat list locally
      const updatedChats = state.chats?.map(chat =>
        chat.id === state.selectedChat?.id
          ? {
              ...chat,
              lastMessageText: message,
              lastMessageTime: currentTime
            }
          : chat
      );

      updateState({chats: updatedChats});

      try {
        const createdMessage = await MessagesRequests.sendMessage(data);

        // Merge created message into current messages without losing local state
        setState(prev => {
          const existingMessages = prev.messages ?? [];

          // If message already exists (e.g., came from socket), do nothing
          if (existingMessages.some(m => m.id === createdMessage.id)) {
            return prev;
          }

          const newMessages = [...existingMessages];
          const optimisticIndex = newMessages.findIndex(
            m =>
              m.id === 0 &&
              m.content === optimisticMessage.content &&
              m.chatId === optimisticMessage.chatId &&
              m.authorId === optimisticMessage.authorId
          );

          if (optimisticIndex !== -1) {
            newMessages[optimisticIndex] = createdMessage;
          } else {
            newMessages.unshift(createdMessage);
          }

          return {
            ...prev,
            messages: newMessages
          };
        });
      } catch (err: any) {
        updateState({hasError: err.message});
      }
    }
  }, [state.selectedChat, state.user?.user, state.chats, addMessageToStart, updateState]);

  const addUserToChat = useCallback(async (id: number | undefined) => {
    if(!id) return;
    if (state.selectedChat) {
      try {
        await ChatRequests.addParticipant(state.selectedChat.id, id);
        const chats = await ChatRequests.getChats(page);
        updateState({chats});
      } catch (err: any) {
        updateState({hasError: err.message});
      }
    }
  }, [state.selectedChat, state.user?.id, state.user?.user?.username, state.chatPage, addMessageToStart, updateState]);

  const loadMoreChats = useCallback(() => {
    if (state.loading || !state.hasMore) {
      return;
    }
    updateState({page: state.page + 1});
  }, [state.loading, state.hasMore, state.page, updateState]);

  return {
    state,
    search,
    goToMain,
    selectChat,
    goBack,
    sendMessage,
    addUserToChat,
    loadMoreChats
  };
};

export default useChats;